import type { Matcher } from "react-day-picker";

import { FilterType } from "./filters.types";
import { getCurrentFortnight } from "./FortnightPicker";

import { FILTER_WIDTHS, ZERO } from "@/app/constants/constants";
import type { useQueryFilterType } from "@/hooks/query/useQueryParams";
import { Any } from "@/interfaces/globals";
import { sanitizeBrowserQueryParams } from "@/utils/form/queryParams";

/**
 * Formats a fortnight boundary date for browser query params.
 *
 * @param {Date | undefined} date - Fortnight start or end date.
 * @returns {string} ISO date string or empty string when invalid.
 */
export const formatFortnightQueryDate = (date: Date | undefined): string => {
  return date && !Number.isNaN(date.getTime()) ? date.toISOString() : "";
};

/**
 * Builds URL params with only `ig_*` keys and the current fortnight range.
 *
 * @param {URLSearchParams} currentParams - Browser query params before clearing.
 * @returns {URLSearchParams} Cleared params ready for the router.
 */
export const buildClearedFortnightSearchParams = (
  currentParams: URLSearchParams,
): URLSearchParams => {
  const clearedParams = new URLSearchParams();
  const sanitizedParams = sanitizeBrowserQueryParams(currentParams);

  sanitizedParams.forEach((value, key) => {
    if (key.startsWith("ig_")) {
      clearedParams.append(key, value);
    }
  });

  const { start, end } = getCurrentFortnight();
  const startDateValue = formatFortnightQueryDate(new Date(start));
  const endDateValue = formatFortnightQueryDate(new Date(end));

  if (startDateValue) {
    clearedParams.append("fortnight", startDateValue);
  }

  if (endDateValue) {
    clearedParams.append("fortnight", endDateValue);
  }

  return sanitizeBrowserQueryParams(clearedParams);
};

/**
 * Clears all user-applied filters and sets fortnight to the current period.
 * Preserves `ig_*` UI mirror params such as the active tab.
 *
 * @param {URLSearchParams} params - Browser query params to mutate in place.
 * @returns {void}
 */
export const applyClearedFortnightFilterParams = (
  params: URLSearchParams,
): void => {
  const rebuiltParams = buildClearedFortnightSearchParams(params);

  Array.from(new Set(Array.from(params.keys()))).forEach((key) => {
    params.delete(key);
  });

  rebuiltParams.forEach((value, key) => {
    params.append(key, value);
  });
};

/**
 * Removes a filter key and any related label or linked keys from URL params.
 *
 * @param {URLSearchParams} params - Browser query params to mutate in place.
 * @param {string} filterKey - Primary filter key to remove.
 * @param {string | undefined} linkedFilterKey - Optional linked filter key.
 * @returns {void}
 */
export const applyClearFilterParam = (
  params: URLSearchParams,
  filterKey: string,
  linkedFilterKey?: string,
): void => {
  params.delete(filterKey);
  params.delete(`${filterKey}_label`);

  if (linkedFilterKey) {
    params.delete(linkedFilterKey);
    params.delete(`${linkedFilterKey}_label`);
  }
};

/**
 * Checks if all URL parameter values are null or empty.
 *
 * @param {Record<string, string | null | undefined>} params - URL query parameters.
 * @returns {boolean} True when every value is null or empty string.
 */
export const areAllValuesNull = (
  params: Record<string, string | null | undefined>,
): boolean =>
  params &&
  Object.values(params).every((value) => value === null || value === "");

/**
 * Returns true when non-search, non-ignored keys have values — meaning a clear button is warranted.
 *
 * @param {Record<string, string | null | undefined>} params - URL query parameters.
 * @param {string[]} ignoreKeys - Keys to exclude from the check.
 * @returns {boolean} True when at least one filterable key has a non-empty value.
 */
export const shouldShowClearFilter = (
  params: Record<string, string | null | undefined>,
  ignoreKeys: string[],
): boolean => {
  const otherKeys =
    params && Object.keys(params).filter((key) => !ignoreKeys.includes(key));

  const nonSearchKeys = otherKeys?.filter((key) => {
    return !key.includes("_search") && key !== "search";
  });

  return (
    nonSearchKeys?.some((key) => {
      return (
        params[key] !== null && params[key] !== undefined && params[key] !== ""
      );
    }) ?? false
  );
};

/**
 * Returns the estimated width for a filter type if no real measurement is available.
 *
 * @param {string} key - The filter key.
 * @param {Any} value - The filter configuration value.
 * @returns {number} The estimated width in pixels.
 */
const getFallbackWidth = (key: string, value: Any): number => {
  if (key === "fortnight") {
    return FILTER_WIDTHS.FORTNIGHT;
  }
  if (key === "yearSliderSelector") {
    return FILTER_WIDTHS.YEAR_SLIDER;
  }
  if (
    key === "dateRangeWithPresets" ||
    key === "dateRange" ||
    value?.isDateDropdown ||
    value?.isDateRangePresets
  ) {
    return FILTER_WIDTHS.DATE_RANGE;
  }
  return FILTER_WIDTHS.STANDARD;
};

interface IGetSliceCount<T, P> {
  width: number;
  filters: FilterType<T, P>;
  measuredWidths: Record<string, number>;
}

/**
 * Determines visible filter count based on estimated widths and available space.
 *
 * @param {IGetSliceCount} props - Configuration for slice count calculation.
 * @returns {number} Number of filter entries to render inline before overflow.
 */
export const getSliceCount = <T, P>({
  width,
  filters,
  measuredWidths,
}: IGetSliceCount<T, P>): number => {
  if (width <= ZERO) {
    return ZERO;
  }

  let availableWidth = width;
  const GAP = FILTER_WIDTHS.GAP;

  const EXCLUDED_KEYS = ["limit", "offset", "currentPage"];

  const filterEntries = Object.entries(filters).filter(
    ([key]) => !EXCLUDED_KEYS.includes(key),
  );

  // Pre-calculate filter widths
  const filterWidths = filterEntries.map(([key, value]) => {
    return measuredWidths[key] ?? getFallbackWidth(key, value);
  });

  // Total Required Width without space for more button
  const totalRequiredWidth = filterWidths.reduce(
    (acc, w) => acc + w + GAP,
    ZERO,
  );

  // Show all filters if enough space is available
  if (totalRequiredWidth <= availableWidth) {
    return filterEntries.length;
  }

  // reserve "more" button space only when slicing is needed
  availableWidth -= FILTER_WIDTHS.MORE_BUTTON + GAP;

  if (availableWidth <= ZERO) {
    return ZERO;
  }

  // Calculate Visible Count
  let visibleCount = ZERO;
  let currentUsedWidth = ZERO;

  for (let i = ZERO; i < filterEntries.length; i++) {
    const filterWidth = filterWidths[i];

    // Increase count if availableWidth has space for the current filter
    if (currentUsedWidth + filterWidth + GAP <= availableWidth) {
      currentUsedWidth += filterWidth + GAP;
      visibleCount++;
    } else {
      break;
    }
  }

  return visibleCount;
};

/**
 * Extracts date range matchers from URL query parameters.
 *
 * @param {(key: string) => string[] | null} getAllValue - Accessor for multi-value query params.
 * @returns {{ dateTypeValues: string[]; dateRangeMatcher: Matcher[]; dateRangeVal: { from: Date; to: Date } | undefined; dateRangeValues: string[] | undefined; }} Parsed date range state.
 */
export const getDateRangeMatchers = (
  getAllValue: useQueryFilterType["getAllValue"],
): {
  dateTypeValues: string[];
  dateRangeMatcher: Matcher[];
  dateRangeVal: { from: Date; to: Date } | undefined;
  dateRangeValues: string[] | undefined;
} => {
  const dateRangeValues = getAllValue("dateRange") ?? undefined;
  const dateTypeValues = getAllValue("dateType") ?? [];
  const [startDateRangeValue, endDateRangeValue] =
    dateRangeValues && dateRangeValues.length >= 2
      ? [dateRangeValues[0], dateRangeValues[dateRangeValues.length - 1]]
      : [];

  const dateRangeVal =
    startDateRangeValue && endDateRangeValue
      ? {
          from: new Date(startDateRangeValue),
          to: new Date(endDateRangeValue),
        }
      : undefined;

  const matcherStartDate = getAllValue("matcherStartDate");
  const matcherEndDate = getAllValue("matcherEndDate");

  let dateRangeMatcher: Matcher[] = [];

  if (matcherStartDate && matcherEndDate) {
    dateRangeMatcher = [
      {
        before: new Date(matcherStartDate[0]),
        after: new Date(matcherEndDate[0]),
      },
    ];
  }

  return { dateTypeValues, dateRangeMatcher, dateRangeVal, dateRangeValues };
};
